import { WorkingWithUsPageProps } from '../types/types';
import JourneySection from './components/JourneySection/JourneySection';
import { useScrollToNextTarget } from './hooks/useScrollToNextTarget';
import BenefitsMembershipSection from './sections/benefits-membership/benefits-membership';
import CTAFinancialClarity from './sections/cta-financial-clarity/cta-financial-clarity';
import FinancialPlanSection from './sections/financial-plan/financial-plan';
import GenuineConversations from './sections/genuine-conversations/genuine-conversations';
import HeroSection from './sections/hero/hero';
import HighlightSection from './sections/highlight/highlight';
import StarSection from './sections/star/star';
import WhatWeDoSection from './sections/what-we-do/what-we-do';
import IconArrow from './svg/icon-arrow';
import IconStar from './svg/icon-star';
import {
  Container,
  PageSection,
  StaticIndicator,
} from './working-with-us.style';
import { DisclaimerSection } from '@exploration/shared-sections';
import { NextPageWithLayout } from '@exploration/shared-types';
import { ForceBreakpointProvider, useBreakpoint } from '@exploration/ui-utils';
import { useEffect, useRef } from 'react';
import { useTheme } from 'styled-components';

// --------------------------------------------------------------------------

export const WorkingWithUsPage: NextPageWithLayout<WorkingWithUsPageProps> = ({
  flags,
  heroSectionData,
  whatWeDoSectionData,
  genuineConversationsData,
  starSectionData,
  ctaSectionData,
  financialPlanSectionData,
  benefitMembershipSectionData,
  highlightData,
  disclaimerData,
  forceBreakpoint = 'none',
}) => {
  const { midwestColors: colors } = useTheme();
  const bp = useBreakpoint();
  const isMobile = bp === 'mobile';
  const isTablet = bp === 'tablet';
  const isMobileOrTablet = isMobile || isTablet;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const staticIndicatorRef = useRef(null);
  const {
    stickyElementRef,
    targetElementsRef,
    scrollToNextTarget,
    scrollContainerRef,
  } = useScrollToNextTarget();
  PageSection.defaultProps = {
    forceMobileBreakpoint: forceBreakpoint === 'mobile',
  };

  useEffect(() => {
    const targets = document.querySelectorAll<HTMLElement>('.indicator');
    targetElementsRef.current = targets;

    stickyElementRef.current = staticIndicatorRef.current;
    if (containerRef.current) {
      scrollContainerRef.current = containerRef.current.parentElement!;
    }
  }, [targetElementsRef, stickyElementRef, scrollContainerRef]);

  return (
    <ForceBreakpointProvider bp={forceBreakpoint}>
      <Container ref={containerRef}>
        {forceBreakpoint === 'mobile' && (
          <StaticIndicator
            ref={staticIndicatorRef}
            onClick={scrollToNextTarget}
            tabIndex={0}
            aria-label="arrow-journey-button"
            data-analytics-cta-text="arrow-journey-button"
          >
            <IconArrow />
          </StaticIndicator>
        )}
        <PageSection isBgGradient>
          {forceBreakpoint === 'none' ? (
            <JourneySection
              isInverse
              indicator={<IconArrow />}
              isStart
              isStacked
            >
              <HeroSection {...heroSectionData} />
            </JourneySection>
          ) : (
            <HeroSection {...heroSectionData} />
          )}
        </PageSection>
        <PageSection>
          <JourneySection
            indicator="1"
            isStart={isMobileOrTablet || forceBreakpoint === 'mobile'}
            staticIndicatorRef={staticIndicatorRef}
            containerRef={containerRef}
          >
            <WhatWeDoSection {...whatWeDoSectionData} />
          </JourneySection>
        </PageSection>
        <PageSection isBgDark>
          <JourneySection
            indicator="2"
            staticIndicatorRef={staticIndicatorRef}
            containerRef={containerRef}
          >
            <GenuineConversations {...genuineConversationsData} />
          </JourneySection>
        </PageSection>
        <PageSection isBgDark>
          <JourneySection
            indicator="3"
            staticIndicatorRef={staticIndicatorRef}
            containerRef={containerRef}
          >
            <FinancialPlanSection {...financialPlanSectionData} />
          </JourneySection>
        </PageSection>
        <PageSection isBgDark>
          <JourneySection
            isInverse
            indicator={<IconStar />}
            staticIndicatorRef={staticIndicatorRef}
            containerRef={containerRef}
          >
            <StarSection {...starSectionData} />
          </JourneySection>
        </PageSection>
        <PageSection>
          <JourneySection
            indicator="4"
            staticIndicatorRef={staticIndicatorRef}
            containerRef={containerRef}
            isEnd
          >
            <BenefitsMembershipSection {...benefitMembershipSectionData} />
          </JourneySection>
        </PageSection>
        <PageSection data-testid="financial-clarity">
          <CTAFinancialClarity {...ctaSectionData} />
        </PageSection>
        {highlightData && (
          <PageSection data-testid="nbe-highlight">
            <HighlightSection {...highlightData} />
          </PageSection>
        )}
        <DisclaimerSection
          disclaimers={[disclaimerData?.headline ?? disclaimerData?.text ?? '']}
          backgroundColor={colors.backgroundSecondary}
        />
      </Container>
    </ForceBreakpointProvider>
  );
};
